import { isStorageItem } from '../utils/isStorageItem';
import { defaultLocation } from '../const/defaultLocation';

const urlService = {
  challenge: phone_number => ({
    url: '/knock/challenge',
    params: {
      phone: `+${phone_number}`,
    },
  }),
  code: (phone_number, solution) => ({
    url: '/sms',
    params: {
      phone: `+${phone_number}`,
      solution: solution,
    },
  }),
  verify: (phone_number, token, code, deviceObj) => ({
    url: '/sms/verify',
    method: 'POST',
    params: {
      phone: `+${phone_number}`,
    },
    data: {
      token: token, // bearer token returned in the last step
      solution: code, // 6-digit verification code received via sms
      sales_channel: 'qr_code',
      create_guest: true,
      ...deviceObj,
    },
  }),
  universalLabel: alias => ({ url: `/v1/locations?alias=%23${alias}` }),
  getMapboxLocation: ({ query, longitude, latitude }) => {
    const userLocation = isStorageItem('userLocation');
    longitude = longitude || userLocation?.longitude || defaultLocation.longitude;
    latitude = latitude || userLocation?.latitude || defaultLocation.latitude;
    return {
      url: `/geocoding/v5/mapbox.places/${query}.json`,
      params: {
        types: 'place,postcode,address,poi',
        limit: 10,
        ...((longitude || latitude) && { proximity: `${parseFloat(longitude)},${parseFloat(latitude)}` }),
      },
    };
  },
  getMapboxEta: ({ startLongitude, endLatitude, startLatitude, endLongitude }) => ({
    url: `/directions/v5/mapbox/driving/${startLongitude},${startLatitude};${endLongitude},${endLatitude}`,
  }),
  estimateFare: ({ pickupAlias, pickup, dropoff, service_type }) => ({
    url: '/v2/estimates/fare',
    params: {
      booking_channel: 'rider_web',
      ...(pickupAlias && { 'pickup_location[alias]': `#${pickupAlias}` }),
      ...(!pickupAlias && { pickup_latitude: pickup.latitude }),
      ...(!pickupAlias && { pickup_longitude: pickup.longitude }),
      dropoff_latitude: dropoff.latitude,
      dropoff_longitude: dropoff.longitude,
      service_type: service_type || 'taxi',
      number_of_passengers: 1,
    },
  }),
  estimateFareWithServiceTypes: ({ estimates_token, selectedFleet, payment, pickup, dropoff }) => ({
    url: `/v1/bookable/estimates`,
    params: {
      estimates_token: estimates_token,
      ...(selectedFleet?.id && { provider_id: selectedFleet.id }),
      ...(payment?.type && { payment_type: payment.type }),
      'pickup_location[latitude]': pickup.latitude,
      'pickup_location[longitude]': pickup.longitude,
      'pickup_location[line1]': pickup.line1,
      'pickup_location[city]': pickup.city,
      'pickup_location[postal_code]': pickup.postal_code,
      'pickup_location[state]': pickup.state,
      'dropoff_location[latitude]': dropoff.latitude,
      'dropoff_location[longitude]': dropoff.longitude,
      'dropoff_location[line1]': dropoff.line1,
      'dropoff_location[city]': dropoff.city,
      'dropoff_location[postal_code]': dropoff.postal_code,
      'dropoff_location[state]': dropoff.state,
    },
  }),
  riderUpfrontFareForRide: ride_id => ({
    url: `/v1/estimates/rides/${ride_id}/fare`,
  }),
  bookRide: ({
    pickupAlias,
    pickup: { text: pkd, simple: pks = {} } = {},
    dropoff: { text: drd, simple: drs = {} } = {},
    estimate_id,
    total_amount,
    paymentMethodId,
    isAccessibleVehicle,
    defaultServiceType,
    driverNote,
    isStartedAsGuest,
    isWebBooker,
    pickup_time,
    pickup_time_offset_from_local,
    selectedFleet,
  }) => ({
    url: '/v2/rides',
    method: 'POST',
    data: {
      pickup_location: {
        line1: pks.line1,
        city: pks.city,
        state: pks.state,
        postal_code: pks.postalCode || pks.postal_code,
        displayed_address: pkd,
        ...(pickupAlias && { alias: pickupAlias }),
        ...(!pickupAlias && { latitude: pks.latitude }),
        ...(!pickupAlias && { longitude: pks.longitude }),
      },
      ...(isStartedAsGuest && { sales_channel: 'qr_code' }),
      booking_channel: 'rider_web',
      payment_method_id: paymentMethodId,
      fare_estimate_id: estimate_id,
      service_type: isAccessibleVehicle ? 'wav' : defaultServiceType ?? 'taxi',
      dropoff_location: {
        line1: drs.line1,
        city: drs.city,
        state: drs.state,
        postal_code: drs.postalCode || drs.postal_code,
        displayed_address: drd,
        latitude: drs.latitude,
        longitude: drs.longitude,
      },
      number_of_passengers: 1,
      accepted_fare_amount: total_amount,
      ...(driverNote && { special_instructions: driverNote }),
      ...(selectedFleet?.id && { provider_id: selectedFleet.id }),
      pickup_time,
      pickup_time_offset_from_local,
    },
  }),
  getProfile: () => ({ url: 'v1/users/profile' }),
  updateUserTips: (defaultTip, defaultTipType) => ({
    url: 'v3/users/profile',
    method: 'PUT',
    data: {
      default_tip: defaultTip,
      default_tip_type: defaultTipType,
    },
  }),
  updateProfile: ({ name: { first, last }, email, password }) => ({
    url: 'v1/users/profile',
    method: 'PUT',
    data: {
      first_name: first,
      last_name: last,
      email,
      password,
      sign_up_channel: 'web_booking',
    },
  }),
  newFare: (ride_id, { simple: { latitude, longitude, line1, city, postal_code, state } = {} }) => ({
    url: `v1/rides/${ride_id}/calc_new_fare`,
    params: {
      'dropoff_location[latitude]': latitude,
      'dropoff_location[longitude]': longitude,
      'dropoff_location[line1]': line1,
      'dropoff_location[city]': city,
      'dropoff_location[postal_code]': postal_code,
      'dropoff_location[state]': state,
    },
  }),
  updateDestination: (ride_id, dropoff, fare_estimate_id) => ({
    url: `v1/rides/${ride_id}`,
    method: 'PUT',
    data: {
      dropoff_location: dropoff,
      fare_estimate_id,
    },
  }),
  cancelRide: (ride_id, params) => ({
    url: `v1/rides/${ride_id}`,
    method: 'DELETE',
    params: params,
  }),
  sendNote: (ride_id, msg) => ({
    url: `v1/rides/${ride_id}/message_to_vehicle`,
    method: 'POST',
    data: {
      message: msg,
    },
  }),
  getWay2IToken: () => ({ url: `v1/users/token?target=way2i` }),
  addPaymentMethod: data => ({
    url: `v1/payment_methods`,
    method: 'POST',
    data: data,
  }),
  removePaymentMethod: cardId => ({
    url: `v1/payment_methods/${cardId}`,
    method: 'DELETE',
  }),
  setAsDefaultPaymentMethod: cardId => ({
    url: `v1/payment_methods/${cardId}/set_as_default`,
    method: 'PUT',
  }),
  loadPersonalPayments: userId => ({ url: `v1/payment_methods?user_id=${userId}` }),
  loadPaymentsUsingProviderId: providerId => ({
    url: `v1/payment_methods?provider_id=${providerId}&booking_channel=rider_web`,
  }),
  getRide: ride_id => ({ url: `v1/rides/${ride_id}` }),
  getRideFromList: ride_id => ({ url: `v3/rides?search[id]=${ride_id}` }),
  updateTips: (ride_id, tipsParam) => ({
    url: `v1/rides/${ride_id}`,
    method: 'PUT',
    data: {
      ...tipsParam,
    },
  }),
  getRideDetails: ride_id => ({
    url: `v1/rides/${ride_id}/details?raw=true&ride_events=true`,
  }),
  sendEReceipt: (ride_id, email_id) => ({
    url: `v1/rides/${ride_id}/send_ereceipt?email=${email_id}`,
    method: 'PUT',
  }),
  getServiceTypes: (lat, lng, paymentId) => ({
    // url: `v1/locations/service_types?latitude=${lat}&longitude=${lng}`,
    url: `v2/rides/bookable`,
    params: {
      latitude: lat,
      longitude: lng,
      ...(paymentId && { payment_id: paymentId }),
    },
  }),
  loadVehicleInfo: ({ providerKey, vehicleNumber }) => ({
    url: `/providers/${providerKey}/vehicles/${vehicleNumber}?include_metadata=true`,
  }),
  getTimeZone: postalCode => ({
    url: `/locations/postal_codes/${postalCode}/time_zone`,
  }),
  gerUserRides: ({ filterBy, userId, search_ride_status }) => ({
    url: `v3/rides`,
    params: {
      filter_by: filterBy,
      user_id: userId,
      'search[ride_status_code]': search_ride_status,
      order: 'desc',
    },
  }),
  getProviders: ({
    pickup_latitude,
    pickup_longitude,
    service_type,
    payment_method_id,
    pickup_time,
    dropoff_latitude,
    dropoff_longitude,
  }) => ({
    url: `v1/providers`,
    params: {
      pickup_latitude,
      pickup_longitude,
      service_type,
      payment_method_id,
      pickup_time,
      dropoff_latitude,
      dropoff_longitude,
    },
  }),
  getShareLink: rideId => ({
    url: `/rides/${rideId}/tracker_link`,
  }),
  reverseGeocoding: (lat, lng) => {
    const userLocation = isStorageItem('userLocation');
    let longitude = lng || userLocation?.longitude;
    let latitude = lat || userLocation?.latitude;
    return {
      url: `/geocoding/v5/mapbox.places/${longitude},${latitude}.json`,
      params: {
        types: 'place,postcode,address,poi',
      },
    };
  },
  updateProfileSms: sms_enabled => ({
    url: 'v3/users/profile',
    method: 'PUT',
    data: {
      sms_enabled: sms_enabled,
    },
  }),
};

export default urlService;
